// CustomWebsiteModal.js - The Ownezzy site's free trial sign-up: the shared SignupFlow (shared/signup/, the same one
// the demo stores use) with this site's data: the demo design picked, or a business type for a custom website.

import React from 'react';
import { POPULAR_CATEGORIES } from '../utils/categoriesData';
import { trackGetFreeClick } from '../utils/track';
import SignupFlow from '../shared/signup/SignupFlow';
import { heroTemplates, demoStoreUrl } from './Hero';

const TEMPLATE_KEYS_SET = new Set([
  'clothing', 'blouses', 'tech', 'footwear', 'uruli', 'linens',
  'plants', 'gifts', 'pets', 'aquarium', 'selfcare', 'grocery',
  'fresh', 'bags', 'crafts', 'decor', 'favors', 'resin', 'crochet',
  'pattu', 'tshirts', 'acrylic', 'beauty', 'cosmetics', 'kurti', 'perfume',
  'toys', 'medcare', 'dryfruits', 'bedbath', 'lungi', 'organic', 'murti', 'spices', 'snacks', 'teacoffee', 'gardentools',
  'pickles', 'bakery', 'jewellery', 'sarees', 'sweets', 'herbal', 'healthmix', 'dailywear', 'tiffin', 'bangles',
  'pooja', 'electronics', 'comics', 'coconutoil', 'cleaning', 'artjewellery', 'wallclocks', 'mirrors', 'candles', 'custom',
]);

const TEMPLATE_ALIAS_MAP = {
  wellness: 'selfcare',
  soaps: 'selfcare',
  soap: 'selfcare',
  cases: 'tech',
  case: 'tech',
  mobile: 'tech',
  blouse: 'blouses',
  jutti: 'footwear',
  shoes: 'footwear',
  shoe: 'footwear',
  dohar: 'linens',
  bedsheet: 'linens',
  saree: 'clothing',
  kurti: 'clothing',
  apparel: 'clothing',
  fish: 'aquarium',
  pet: 'pets',
  plant: 'plants',
  vegetable: 'fresh',
  fruits: 'fresh',
  spices: 'grocery',
  tshirt: 'tshirts',
  tee: 'tshirts',
  tees: 'tshirts',
  frame: 'acrylic',
  frames: 'acrylic',
  photo: 'acrylic',
  makeup: 'cosmetics',
  cosmetic: 'cosmetics',
  kurtis: 'kurti',
  perfumes: 'perfume',
  attar: 'perfume',
  ittar: 'perfume',
  toy: 'toys',
  medical: 'medcare',
  pharmacy: 'medcare',
  healthcare: 'medcare',
  nuts: 'dryfruits',
  'dry fruits': 'dryfruits',
  towel: 'bedbath',
  towels: 'bedbath',
  gamcha: 'lungi',
  gamosa: 'lungi',
  lungis: 'lungi',
  dhoti: 'lungi',
  idol: 'murti',
  idols: 'murti',
  murtis: 'murti',
  sculpture: 'murti',
  statue: 'murti',
  masala: 'spices',
  spice: 'spices',
  chips: 'snacks',
  namkeen: 'snacks',
  snack: 'snacks',
  tea: 'teacoffee',
  coffee: 'teacoffee',
  chai: 'teacoffee',
  'garden tools': 'gardentools',
  tools: 'gardentools',
  pickle: 'pickles',
  podi: 'pickles',
  cake: 'bakery',
  cakes: 'bakery',
  jewelry: 'jewellery',
  saree: 'sarees',
  sweet: 'sweets',
  mithai: 'sweets',
  'hair oil': 'herbal',
  'health mix': 'healthmix',
  nighty: 'dailywear',
  nighties: 'dailywear',
  tiffin: 'tiffin',
  catering: 'tiffin',
  bangle: 'bangles',
  pooja: 'pooja',
  puja: 'pooja',
  electronics: 'electronics',
  gadgets: 'electronics',
  comics: 'comics',
  comic: 'comics',
  manga: 'comics',
  'coconut oil': 'coconutoil',
  cleaning: 'cleaning',
  'artificial jewellery': 'artjewellery',
  'handmade jewellery': 'artjewellery',
  clock: 'wallclocks',
  clocks: 'wallclocks',
  mirror: 'mirrors',
  mirrors: 'mirrors',
  candle: 'candles',
  candles: 'candles',
};

export function resolveTemplate(categoryName = '', explicitTemplate = '') {
  const normExplicit = String(explicitTemplate || '').toLowerCase().trim();
  if (normExplicit) {
    if (TEMPLATE_KEYS_SET.has(normExplicit)) return normExplicit;
    if (TEMPLATE_ALIAS_MAP[normExplicit]) return TEMPLATE_ALIAS_MAP[normExplicit];
  }

  const normCat = String(categoryName || '').toLowerCase().trim();
  if (!normCat) return 'custom';

  const heroMatch = heroTemplates.find((t) => {
    const id = (t.id || '').toLowerCase();
    const name = (t.name || '').toLowerCase();
    const label = (t.label || '').toLowerCase();
    const business = (t.business || '').toLowerCase();
    return normCat === id || normCat === name || normCat === label || normCat === business ||
           normCat.includes(label) || label.includes(normCat);
  });
  if (heroMatch) {
    const tpl = heroMatch.template || heroMatch.id;
    if (TEMPLATE_KEYS_SET.has(tpl)) return tpl;
    if (TEMPLATE_ALIAS_MAP[tpl]) return TEMPLATE_ALIAS_MAP[tpl];
  }

  const catMatch = POPULAR_CATEGORIES.find((c) => {
    const id = (c.id || '').toLowerCase();
    const name = (c.name || '').toLowerCase();
    return normCat === id || normCat === name || normCat.includes(name) || name.includes(normCat);
  });
  if (catMatch && catMatch.template && TEMPLATE_KEYS_SET.has(catMatch.template)) {
    return catMatch.template;
  }

  if (/wall clock|clocks?\b/i.test(normCat)) return 'wallclocks';
  if (/mirrors?\b/i.test(normCat)) return 'mirrors';
  if (/candles?\b|wax melt|tealight/i.test(normCat)) return 'candles';
  if (/comic|manga|graphic novel/i.test(normCat)) return 'comics';
  if (/electronic|gadget|earbud|headphone|smart ?watch|charger|power bank|bluetooth speaker/i.test(normCat)) return 'electronics';
  if (/coconut oil|virgin coconut|wood.?pressed coconut/i.test(normCat)) return 'coconutoil';
  if (/toilet|bathroom clean|cleaning|cleaner|phenyl|mop|disinfectant/i.test(normCat)) return 'cleaning';
  if (/artificial jewel|handmade jewel|fashion jewel|resin earring|beaded jewel|handmade earring/i.test(normCat)) return 'artjewellery';
  if (/pooja|puja|diya|agarbatti|incense|rangoli|mandir/i.test(normCat)) return 'pooja';
  if (/custom kurti|kurti stitch|tailor|boutique stitch/i.test(normCat)) return 'kurti';
  if (/cosmetic|makeup|make-up|lipstick|nail polish|beauty brand/i.test(normCat)) return 'cosmetics';
  if (/fashion (and|&) beauty|beauty store|beauty (and|&) fashion/i.test(normCat)) return 'beauty';
  if (/t-?shirts?|\btees?\b|printed shirt|hoodie/i.test(normCat)) return 'tshirts';
  if (/acrylic|photo frame|wall photo|wall art|wall clock|photo collage/i.test(normCat)) return 'acrylic';
  if (/blouse|aari|maggam/i.test(normCat)) return 'blouses';
  if (/case|mobile cover|phone skin/i.test(normCat)) return 'tech';
  if (/footwear|jutti|mojari|sandals?|shoes?/i.test(normCat)) return 'footwear';
  if (/linen|bedsheet|dohar|quilt/i.test(normCat)) return 'linens';
  if (/pattu|pavadai|kids ethnic/i.test(normCat)) return 'pattu';
  if (/crochet|yarn|amigurumi/i.test(normCat)) return 'crochet';
  if (/resin|varmala|garland/i.test(normCat)) return 'resin';
  if (/favor|thamboolam|return gift/i.test(normCat)) return 'favors';
  if (/uruli|brass|pooja|vilakku/i.test(normCat)) return 'uruli';
  if (/handbag|tote|backpack|slings?/i.test(normCat)) return 'bags';
  if (/craft|resin art|handmade craft/i.test(normCat)) return 'crafts';
  if (/decor|candle|soy candle|lamp/i.test(normCat)) return 'decor';
  if (/perfume|attar|ittar|fragrance|scent|name soap|custom(ised|ized)? soap|engraved soap/i.test(normCat)) return 'perfume';
  if (/\btoys?\b|teddy|soft toy|ride.?on/i.test(normCat)) return 'toys';
  if (/medical|medicine|pharma|health ?care|surgical|first aid|oximeter|bp monitor|wheelchair/i.test(normCat)) return 'medcare';
  if (/dry ?fruit|cashew|almond|pistachio|walnut|dates|anjeer/i.test(normCat)) return 'dryfruits';
  if (/towel|bath ?robe|bed ?sheet|pillow|comforter/i.test(normCat)) return 'bedbath';
  if (/gamcha|gamosa|lungi|dhoti|mundu|veshti/i.test(normCat)) return 'lungi';
  if (/organic|cold.?pressed|millet|jaggery|herbal tea/i.test(normCat)) return 'organic';
  if (/murti|idol|sculpture|statue/i.test(normCat)) return 'murti';
  if (/pickle|podi|thokku|avakaya|gongura/i.test(normCat)) return 'pickles';
  if (/cake|bakery|baker|cupcake|brownie|cookie/i.test(normCat)) return 'bakery';
  if (/jewel|jhumka|necklace|kemp|temple set|earring/i.test(normCat)) return 'jewellery';
  if (/sweets?|mithai|laddu|ladoo|mysore pak|halwa|barfi/i.test(normCat)) return 'sweets';
  if (/hair oil|herbal|face pack|shikakai|kumkumadi/i.test(normCat)) return 'herbal';
  if (/health mix|sathu maavu|baby food|millet|ragi/i.test(normCat)) return 'healthmix';
  if (/night(y|ie|wear)|leggings|lounge|daily wear|tirupur/i.test(normCat)) return 'dailywear';
  if (/tiffin|meals?|catering|home food|cloud kitchen/i.test(normCat)) return 'tiffin';
  if (/bangle|gajra|jasmine|hair accessor|bridal accessor/i.test(normCat)) return 'bangles';
  if (/saree|sari\b|kanchipuram|handloom/i.test(normCat)) return 'sarees';
  if (/spice|masala|turmeric|chilli powder/i.test(normCat)) return 'spices';
  if (/chips|fries|namkeen|bhujia|snacks?|popcorn|nachos/i.test(normCat)) return 'snacks';
  if (/\btea\b|coffee|chai|brew/i.test(normCat)) return 'teacoffee';
  if (/garden(ing)? tools?|pruner|secateur|hose|sprinkler|lawn/i.test(normCat)) return 'gardentools';
  if (/soap|skincare|handwash|wellness/i.test(normCat)) return 'selfcare';
  if (/plant|gardening|succulent|seed|pot/i.test(normCat)) return 'plants';
  if (/pet|dog|puppy|cat\b/i.test(normCat)) return 'pets';
  if (/aquarium|fish tank|live fish/i.test(normCat)) return 'aquarium';
  if (/grocery|pulses|spices|dry fruit/i.test(normCat)) return 'grocery';
  if (/fruit|vegetable|greens|fresh produce/i.test(normCat)) return 'fresh';
  if (/clothing|kurti|saree|lehenga|ethnic wear|apparel|dress/i.test(normCat)) return 'clothing';
  if (/gift|hamper|keepsake/i.test(normCat)) return 'gifts';

  return 'custom';
}

const CATEGORY_OPTIONS = POPULAR_CATEGORIES.map((c) => ({ name: c.name, icon: c.icon, template: c.template }));

// The demo design a template key stands for, or null for a custom website
function designFor(templateKey) {
  const hero = heroTemplates.find((t) => (t.template || t.id) === templateKey || t.id === templateKey);
  if (!hero) return null;
  return { name: hero.name, icon: hero.icon, label: hero.label, demoUrl: hero.demo ? demoStoreUrl(hero) : '' };
}

export default function CustomWebsiteModal({ isOpen, onClose, initialCategory = '', initialTemplate = '', initialPlan = '', initialLayout = '' }) {
  const templateKey = resolveTemplate(initialCategory, initialTemplate);
  const design = templateKey === 'custom' ? null : designFor(templateKey);

  return (
    <SignupFlow
      open={isOpen}
      onClose={onClose}
      template={templateKey}
      layout={initialLayout}
      category={initialCategory}
      design={design}
      categoryOptions={design ? null : CATEGORY_OPTIONS}
      resolveTemplate={design ? null : (category) => resolveTemplate(category, '')}
      initialPlan={initialPlan}
      logo="/brand/logo-wide-450.webp"
      links={{ terms: '/terms', privacy: '/privacy', plans: '/#plans' }}
      onTrack={(type) => {
        if (type === 'modal_domain_submitted') trackGetFreeClick();
      }}
    />
  );
}
